import { useCallback, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { TypeOf, z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { IActionMetadata } from '@tymely/atoms';
import { BaseModal } from '@tymely/components';

const formSchema = z.object({
    title: z.string().min(1, { message: 'Title is required' }),
    description: z.string().min(1, { message: 'Description is required' }),
    executor_name: z.string().min(1, { message: 'Executor is required' }),
});

type FormInput = TypeOf<typeof formSchema>;

export function ActionMetadataEditDialog(props: {
    open: boolean;
    title: string;
    executorNames: string[];
    actionMetadata: Partial<IActionMetadata>;
    onSubmit: (action: IActionMetadata) => Promise<unknown>;
    onClose: () => void;
}) {
    const [saving, setSaving] = useState(false);

    const {
        register,
        formState: { errors, isValid },
        handleSubmit,
        control,
    } = useForm<FormInput>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            ...props.actionMetadata,
            executor_name: props.actionMetadata.executor_name ?? '',
        },
    });

    const onSubmitHandler: SubmitHandler<FormInput> = useCallback(
        (values: FormInput) => {
            if (!isValid) return;
            setSaving(true);
            props.onSubmit({ ...props.actionMetadata, ...values } as IActionMetadata).finally(() => setSaving(false));
        },
        [isValid, props.actionMetadata],
    );

    return (
        <BaseModal
            open={props.open}
            onClose={props.onClose}
            title={props.title}
            loading={saving}
            onOk={handleSubmit(onSubmitHandler)}
        >
            <form noValidate>
                <TextField
                    required
                    fullWidth
                    autoFocus
                    margin="dense"
                    label="title"
                    type="text"
                    variant="standard"
                    error={!!errors.title}
                    helperText={errors.title?.message || ''}
                    {...register('title')}
                />
                <TextField
                    required
                    fullWidth
                    margin="dense"
                    label="description"
                    type="text"
                    variant="standard"
                    error={!!errors.description}
                    helperText={errors.description?.message || ''}
                    sx={{ mb: 0.5 }}
                    {...register('description')}
                />
                <Controller
                    control={control}
                    name="executor_name"
                    defaultValue={props.actionMetadata.executor_name}
                    render={({ field: { onChange, ...fieldProps }, fieldState: { error } }) => (
                        <Autocomplete
                            size="small"
                            fullWidth
                            options={props.executorNames}
                            disabled={Boolean(props.actionMetadata.id)}
                            onChange={(_, newValue) => {
                                onChange(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={Boolean(error)}
                                    helperText={error?.message || ''}
                                    FormHelperTextProps={{ sx: { ml: 0 } }}
                                    label="Executor*"
                                />
                            )}
                            {...fieldProps}
                            value={fieldProps.value || null}
                            sx={{ mt: 1, maxWidth: '50%' }}
                        />
                    )}
                />
            </form>
        </BaseModal>
    );
}
